import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import API_BASE, {
  getFishingRules,
  getSpeciesByLocation,
  getWeatherByLocation,
  withApiCredentials,
} from "../../api";
import useLocationStateStack from "../../app/useLocationStateStack";
import {
  isFishipediaHistoryTransitionPending,
  submitFishipediaRestoreHistory,
} from "../../app/fishipediaRestoreHistory";
import {
  buildFishipediaRestoreHistoryStates,
  claimFishipediaRestoreAttempt,
  consumeFishipediaTerminalMapParent,
  createMapSpeciesFishipediaReturnContext,
  createPostInfoFishipediaReturnContext,
  FISHIPEDIA_RETURN_CONTEXT_KEY,
  getFishipediaTerminalMapParent,
  getFishipediaRestoreContext,
  resolveFishipediaPostRestoreReadiness,
} from "../../app/fishipediaReturnContext";
import { getCatchSubmitRefreshEventName } from "../../app/catchSubmitGate";
import {
  CLEAR_MAP_SELECTION_EVENT,
  MapStateManager,
} from "../../utils/MapStateManager";
import {
  buildFishSpeciesFilterOptions,
  buildTypeOfFishingFilterOptions,
  buildWayOfFishingFilterOptions,
} from "../../utils/catchMetadataOptions";
import {
  clearPendingMapRestore,
  deferMapRestoreCancellation,
  PENDING_MAP_LOCATION_KEY,
  readPendingMapRestore,
  resumeMapRestoreAttempt,
  scheduleMapRestoreTimeout,
  writePendingMapRestore,
} from "./fishipediaMapRestoreLifecycle";

const ALL_MARKER_FILTER = "all";
const NONE_MARKER_FILTER = "none";
const MARKER_FILTER_MINE = "mine";
const MARKER_FILTER_FRIENDS = "friends";
const MARKER_FILTER_FOLLOWING = "following";
const DEFAULT_PENDING_MAP_LOCATION_ZOOM = 8;
const REFRESH_APP_CHROME_METRICS_EVENT = "reelitpro:refresh-app-chrome-metrics";
const GEOLOCATION_PERMISSION_DENIED = 1;
const GEOLOCATION_POSITION_UNAVAILABLE = 2;
const GEOLOCATION_TIMEOUT = 3;

function clearPendingMapLocationStorage(restoreAttemptId, expectedRaw = null) {
  if (restoreAttemptId) {
    return clearPendingMapRestore(sessionStorage, restoreAttemptId);
  }

  try {
    const raw = sessionStorage.getItem(PENDING_MAP_LOCATION_KEY);
    if (!raw || (expectedRaw && raw !== expectedRaw)) return false;

    try {
      const candidate = JSON.parse(raw);
      if (candidate?.fishipediaRestore === true) return false;
    } catch {
      // Invalid non-owned records are safe to discard.
    }

    if (sessionStorage.getItem(PENDING_MAP_LOCATION_KEY) !== raw) return false;
    sessionStorage.removeItem(PENDING_MAP_LOCATION_KEY);
    return true;
  } catch {
    return false;
  }
}
const PRIMARY_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};
const FALLBACK_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 120000,
};
const GEOLOCATION_PERMISSION_MESSAGE_KEYS = {
  denied: "map_location_permission_denied_help",
  deviceUnavailable: "map_location_device_unavailable",
  unavailable: "map_location_unavailable",
  timeout: "map_location_timeout",
  unsupported: "map_location_unsupported",
};
const GEOLOCATION_PERMISSION_TITLE_KEYS = {
  denied: "map_location_permission_blocked_title",
  deviceUnavailable: "map_location_device_unavailable_title",
  unavailable: "map_location_unavailable_title",
  timeout: "map_location_timeout_title",
  unsupported: "map_location_unsupported_title",
};

function isMobileLocationBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobi|Mobile/i.test(
    navigator.userAgent || ""
  );
}

function requestCurrentPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getGeolocationPermissionState() {
  if (
    typeof navigator === "undefined" ||
    !navigator.permissions ||
    typeof navigator.permissions.query !== "function"
  ) {
    return "unknown";
  }

  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return ["granted", "prompt", "denied"].includes(status?.state)
      ? status.state
      : "unknown";
  } catch {
    return "unknown";
  }
}

function getGeolocationErrorCode(error) {
  const code = Number(error?.code);
  return Number.isFinite(code) ? code : 0;
}

function normalizeFilterValue(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim().replace(/\s+/g, " ");
}

function normalizeFilterKey(value) {
  return normalizeFilterValue(value).toLowerCase();
}

function getCatchSpeciesValue(item) {
  return normalizeFilterValue(item?.catch?.fish || item?.fish || "");
}

function getCatchTypeOfFishingValue(item) {
  return normalizeFilterValue(
    item?.catch?.typeOfFishing || item?.typeOfFishing || ""
  );
}

function getCatchWayOfFishingValue(item) {
  return normalizeFilterValue(
    item?.catch?.wayOfFishing || item?.wayOfFishing || ""
  );
}

function buildAvailableFilterOptions(items, getValue) {
  const optionsByKey = new Map();

  items.forEach((item) => {
    const coordinateKey = buildCoordinateJoinKey(
      item?.catch?.coordinates || item?.coordinates
    );
    if (!coordinateKey) {
      return;
    }

    const value = getValue(item);
    if (!value) {
      return;
    }

    const valueKey = normalizeFilterKey(value);
    if (!optionsByKey.has(valueKey)) {
      optionsByKey.set(valueKey, value);
    }
  });

  return Array.from(optionsByKey.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
}

function buildCatchFilterMetadata(item) {
  return {
    speciesKey: normalizeFilterKey(getCatchSpeciesValue(item)),
    typeOfFishingKey: normalizeFilterKey(getCatchTypeOfFishingValue(item)),
    wayOfFishingKey: normalizeFilterKey(getCatchWayOfFishingValue(item)),
  };
}

function buildCoordinateJoinKey(coordinates, lat, lng) {
  const matches =
    typeof coordinates === "string"
      ? coordinates.match(/(-?\d+(?:\.\d+)?)/g)
      : null;

  if (matches && matches.length >= 2) {
    return `${Number(matches[0]).toFixed(5)},${Number(matches[1]).toFixed(5)}`;
  }

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`;
  }

  return "";
}

function buildSpotJoinKeys({ coordinates, lat, lng, userId, username, user }) {
  const coordinateKey = buildCoordinateJoinKey(coordinates, lat, lng);
  if (!coordinateKey) {
    return [];
  }

  const keys = [];

  if (userId) {
    keys.push(`id:${userId}|${coordinateKey}`);
  }

  const ownerName =
    typeof (username || user) === "string"
      ? (username || user).trim().toLowerCase()
      : "";

  if (ownerName) {
    keys.push(`user:${ownerName}|${coordinateKey}`);
  }

  return keys;
}

function isUnknownPlaceName(value) {
  if (typeof value !== "string") {
    return true;
  }

  const normalized = value.trim().toLowerCase();
  return !normalized || normalized === "unknown" || normalized === "unknown location";
}

function formatMarineBucketName(bucketKey) {
  if (typeof bucketKey !== "string" || !bucketKey.trim()) {
    return "";
  }

  return bucketKey
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function resolveMapPlaceName(weatherData, speciesData) {
  const weatherPlaceName =
    typeof weatherData?.placeName === "string" ? weatherData.placeName.trim() : "";
  if (!isUnknownPlaceName(weatherPlaceName)) {
    return weatherPlaceName;
  }

  const marineZoneName =
    typeof speciesData?.zone_name === "string" ? speciesData.zone_name.trim() : "";
  if (marineZoneName) {
    return marineZoneName;
  }

  const marineBucketName = formatMarineBucketName(speciesData?.contentRef?.bucketKey);
  if (marineBucketName) {
    return marineBucketName;
  }

  return weatherPlaceName;
}

function getPreferredMarkdownPath(markdownPaths, language) {
  if (!markdownPaths || typeof markdownPaths !== "object") {
    return null;
  }

  const preferredLanguage = (language?.split("-")[0] || "en").toLowerCase();
  return (
    markdownPaths[preferredLanguage] ||
    markdownPaths.en ||
    Object.values(markdownPaths).find(Boolean) ||
    null
  );
}

function isBasinContentRef(contentRef) {
  return (
    contentRef?.kind === "basin_waterbody" &&
    typeof contentRef.basePath === "string" &&
    contentRef.basePath.startsWith("/basins/") &&
    typeof contentRef.speciesJsonPath === "string"
  );
}

function isBasinRulesResponse(rulesData) {
  return !!rulesData?.basinSpecific && isBasinContentRef(rulesData?.contentRef);
}

function resolveBasinDisplayName(rulesData, rulesMarkdown) {
  const titleMatch =
    typeof rulesMarkdown === "string" ? rulesMarkdown.match(/^##\s+(.+)$/m) : null;
  const markdownTitle = titleMatch?.[1]
    ? titleMatch[1].replace(/\s+[\u2013\u2014-].*$/, "").trim()
    : "";

  if (markdownTitle) {
    return markdownTitle;
  }

  const waterBodyName =
    typeof rulesData?.waterBodyName === "string" ? rulesData.waterBodyName.trim() : "";
  if (waterBodyName) {
    return waterBodyName;
  }

  const contentName =
    typeof rulesData?.contentRef?.name === "string" ? rulesData.contentRef.name.trim() : "";
  if (contentName) {
    return contentName;
  }

  const bucketKey =
    typeof rulesData?.contentRef?.bucketKey === "string" ? rulesData.contentRef.bucketKey : "";
  return bucketKey ? bucketKey.replace(/_/g, " ") : "";
}

async function loadBasinContentFromRules(rulesData, language) {
  const contentRef = rulesData?.contentRef;
  if (!isBasinContentRef(contentRef)) {
    return null;
  }

  const markdownPath = getPreferredMarkdownPath(contentRef.markdownPaths, language);
  if (!markdownPath) {
    return null;
  }

  const [rulesMarkdown, speciesJson] = await Promise.all([
    fetch(markdownPath).then((response) => {
      if (!response.ok) {
        throw new Error(`Missing rules: ${response.status}`);
      }
      return response.text();
    }),
    fetch(contentRef.speciesJsonPath).then((response) => {
      if (!response.ok) {
        throw new Error(`Missing species.json: ${response.status}`);
      }
      return response.json();
    }),
  ]);

  return {
    displayName: resolveBasinDisplayName(rulesData, rulesMarkdown),
    rulesMarkdown,
    species: Array.isArray(speciesJson?.species) ? speciesJson.species : [],
  };
}

function buildMapOverlayKey(marker, isOpen) {
  if (!isOpen || !Number.isFinite(marker?.lat) || !Number.isFinite(marker?.lng)) {
    return "";
  }

  return `${Number(marker.lat).toFixed(5)},${Number(marker.lng).toFixed(5)}`;
}

function buildSelectedSpotKey(spot) {
  if (!spot) {
    return "";
  }

  if (spot.id) {
    return `id:${spot.id}`;
  }

  if (Number.isFinite(spot.lat) && Number.isFinite(spot.lng)) {
    return `coords:${Number(spot.lat).toFixed(5)},${Number(spot.lng).toFixed(5)}`;
  }

  return "";
}

function restoreSpotFromSnapshot(snapshot, posts) {
  if (!snapshot) return null;
  const reviews = posts.filter((post) => {
    const match = String(post?.catch?.coordinates || "").match(/(-?\d+(\.\d+)?)/g);
    if (!match || match.length < 2) return false;
    return (
      Math.abs(Number(match[0]) - snapshot.lat) < 0.002 &&
      Math.abs(Number(match[1]) - snapshot.lng) < 0.002
    );
  });
  return { ...snapshot, reviews };
}

function isValidMapViewportState(mapState) {
  return (
    typeof mapState?.lat === "number" &&
    Number.isFinite(mapState.lat) &&
    mapState.lat >= -90 &&
    mapState.lat <= 90 &&
    typeof mapState?.lng === "number" &&
    Number.isFinite(mapState.lng) &&
    mapState.lng >= -180 &&
    mapState.lng <= 180 &&
    typeof mapState?.zoom === "number" &&
    Number.isFinite(mapState.zoom) &&
    mapState.zoom >= 2 &&
    mapState.zoom <= 18
  );
}

export function resolveFishipediaMapState(
  mapInstance,
  selectedMapState,
  savedMapState
) {
  try {
    const center = mapInstance?.getCenter?.();
    const liveMapState = {
      lat: center?.lat,
      lng: center?.lng,
      zoom: mapInstance?.getZoom?.(),
      environment: selectedMapState?.environment || null,
    };
    if (isValidMapViewportState(liveMapState)) {
      return liveMapState;
    }
  } catch {
    // Fall through to the most recent validated selected/saved state.
  }

  const fallbackMapState = {
    lat:
      typeof selectedMapState?.lat === "number"
        ? selectedMapState.lat
        : savedMapState?.lat,
    lng:
      typeof selectedMapState?.lng === "number"
        ? selectedMapState.lng
        : savedMapState?.lng,
    zoom: savedMapState?.zoom,
    environment: selectedMapState?.environment || null,
  };

  return isValidMapViewportState(fallbackMapState) ? fallbackMapState : null;
}

export default function useMapContainerState({
  activePage,
  posts = [],
  authoritativePostRestoreResult = null,
  onOpenProfile,
  onOpenGroup,
  onAddFavoriteSpot,
  viewer,
  onUserUpdated,
  isSimpleModeActive = false,
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const fishipediaRestoreContext = useMemo(
    () => getFishipediaRestoreContext(location.state),
    [location.state]
  );
  const mapPostRestoreContext =
    fishipediaRestoreContext?.type === "post-info" &&
    fishipediaRestoreContext.surface === "map"
      ? fishipediaRestoreContext
      : null;
  const mapSpeciesRestoreContext =
    fishipediaRestoreContext?.type === "map-species"
      ? fishipediaRestoreContext
      : null;
  const terminalMapParentRestore = useMemo(
    () => getFishipediaTerminalMapParent(location.state),
    [location.state]
  );
  const mapPostRestoreReadiness = resolveFishipediaPostRestoreReadiness(
    authoritativePostRestoreResult,
    mapPostRestoreContext
  );
  const restoredSelectedSpot = mapPostRestoreContext
    ? restoreSpotFromSnapshot(
        mapPostRestoreContext.selectedSpot,
        mapPostRestoreReadiness.status === "found"
          ? [mapPostRestoreReadiness.post]
          : []
      )
    : terminalMapParentRestore
      ? restoreSpotFromSnapshot(terminalMapParentRestore.selectedSpot, [])
      : null;
  const mapRef = useRef(null);
  const isRestoringRef = useRef(false);
  const saveTimerRef = useRef(null);
  const latestUpdateRequestRef = useRef(0);
  const pendingMapLocationRef = useRef("");
  const mapSpeciesRestoreAttemptRef = useRef("");
  const mapSpeciesRestoreSettlementRef = useRef({
    attemptId: "",
    status: "idle",
  });
  const mapSpeciesRestoreResourcesRef = useRef({
    attemptId: "",
    cancelTimeout: null,
    controller: null,
  });
  const mapHistoryRef = useRef(null);
  const consumedTerminalMapParentRef = useRef("");

  const [mapReady, setMapReady] = useState(false);
  const [selectedMarkerFilters, setSelectedMarkerFilters] = useState([
    ALL_MARKER_FILTER,
  ]);
  const [selectedSpeciesFilters, setSelectedSpeciesFilters] = useState([]);
  const [selectedTypeOfFishingFilter, setSelectedTypeOfFishingFilter] =
    useState("");
  const [selectedWayOfFishingFilter, setSelectedWayOfFishingFilter] =
    useState("");
  const [fishingMethods, setFishingMethods] = useState({});
  const [fishList, setFishList] = useState([]);
  const [catchMarkers, setCatchMarkers] = useState([]);
  const [mapSpots, setMapSpots] = useState([]);
  const [marker, setMarker] = useState(null);
  const [data, setData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(Boolean(mapSpeciesRestoreContext));
  const [overlayTab, setOverlayTab] = useState(
    mapSpeciesRestoreContext ? "fish" : "weather"
  );
  const [selectedSpot, setSelectedSpot] = useState(
    () => restoredSelectedSpot
  );
  const [overlayPanelView, setOverlayPanelView] = useState("tabs");
  const [overlayFish, setOverlayFish] = useState(null);
  const [spotReviewsOpen, setSpotReviewsOpen] = useState(false);
  const [spotSelectedPostInfo, setSpotSelectedPostInfo] = useState(null);
  const [spotDangerOpen, setSpotDangerOpen] = useState(false);
  const [spotExpandedImage, setSpotExpandedImage] = useState(null);
  const [spotExpandedImageSource, setSpotExpandedImageSource] = useState(null);
  const [spotShareOpen, setSpotShareOpen] = useState(false);
  const [spotLocationConsentOpen, setSpotLocationConsentOpen] = useState(false);
  const [locationPermissionDialog, setLocationPermissionDialog] = useState(null);
  const previousOverlayKeyRef = useRef("");
  const previousSelectedSpotKeyRef = useRef("");
  const consumedMapRestoreKeyRef = useRef("");
  const [mapSpeciesRestoreSettlement, setMapSpeciesRestoreSettlement] =
    useState({ attemptId: "", status: "idle" });
  const liveMapSpeciesRestoreAttemptId =
    mapSpeciesRestoreContext?.restoreAttemptId || "";
  mapSpeciesRestoreAttemptRef.current = liveMapSpeciesRestoreAttemptId;
  const mapSpeciesRestoreStatus = !liveMapSpeciesRestoreAttemptId
    ? "idle"
    : mapSpeciesRestoreSettlement.attemptId ===
        liveMapSpeciesRestoreAttemptId
      ? mapSpeciesRestoreSettlement.status
      : "pending";
  const mapSpeciesRestoreReady = mapSpeciesRestoreStatus === "success";
  const mapSpeciesRestoreFailed = mapSpeciesRestoreStatus === "failure";

  const releaseMapSpeciesRestoreResources = useCallback((restoreAttemptId) => {
    const resources = mapSpeciesRestoreResourcesRef.current;
    if (resources.attemptId === restoreAttemptId) {
      resources.cancelTimeout?.();
      resources.controller?.abort();
      mapSpeciesRestoreResourcesRef.current = {
        attemptId: "",
        cancelTimeout: null,
        controller: null,
      };
    }
    clearPendingMapLocationStorage(restoreAttemptId);
    pendingMapLocationRef.current = "";
  }, []);

  const settleMapSpeciesRestore = useCallback(
    (restoreAttemptId, status) => {
      if (
        !restoreAttemptId ||
        mapSpeciesRestoreAttemptRef.current !== restoreAttemptId ||
        !["success", "failure"].includes(status)
      ) {
        return false;
      }

      const currentSettlement = mapSpeciesRestoreSettlementRef.current;
      if (
        currentSettlement.attemptId !== restoreAttemptId ||
        currentSettlement.status !== "pending"
      ) {
        return false;
      }

      mapSpeciesRestoreSettlementRef.current = {
        attemptId: restoreAttemptId,
        status,
      };
      mapSpeciesRestoreAttemptRef.current = "";
      releaseMapSpeciesRestoreResources(restoreAttemptId);
      setMapSpeciesRestoreSettlement({ attemptId: restoreAttemptId, status });
      return true;
    },
    [releaseMapSpeciesRestoreResources]
  );

  useEffect(() => {
    if (!terminalMapParentRestore) return;
    if (
      consumedTerminalMapParentRef.current ===
      terminalMapParentRestore.restoreAttemptId
    ) {
      return;
    }
    consumedTerminalMapParentRef.current =
      terminalMapParentRestore.restoreAttemptId;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: consumeFishipediaTerminalMapParent(location.state),
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    terminalMapParentRestore,
  ]);

  useEffect(() => {
    if (!mapSpeciesRestoreContext) return undefined;

    const { lat, lng, zoom } = mapSpeciesRestoreContext.mapState;
    const restoreAttemptId = mapSpeciesRestoreContext.restoreAttemptId;
    mapSpeciesRestoreAttemptRef.current = restoreAttemptId;
    const existingSettlement = mapSpeciesRestoreSettlementRef.current;
    if (
      existingSettlement.attemptId === restoreAttemptId &&
      ["success", "failure"].includes(existingSettlement.status)
    ) {
      return undefined;
    }
    mapSpeciesRestoreSettlementRef.current = {
      attemptId: restoreAttemptId,
      status: "pending",
    };
    if (!resumeMapRestoreAttempt(window, restoreAttemptId)) {
      settleMapSpeciesRestore(restoreAttemptId, "failure");
      return undefined;
    }

    const controller = new AbortController();
    mapSpeciesRestoreResourcesRef.current = {
      attemptId: restoreAttemptId,
      cancelTimeout: null,
      controller,
    };
    const didWriteRestore = writePendingMapRestore(
      sessionStorage,
      { lat, lng, zoom },
      restoreAttemptId
    );
    if (!didWriteRestore) {
      console.warn("Unable to persist Fishipedia map return state");
      settleMapSpeciesRestore(restoreAttemptId, "failure");
      return undefined;
    }

    const cancelTimeout = scheduleMapRestoreTimeout(
      window,
      () => settleMapSpeciesRestore(restoreAttemptId, "failure")
    );
    if (
      mapSpeciesRestoreResourcesRef.current.attemptId === restoreAttemptId
    ) {
      mapSpeciesRestoreResourcesRef.current.cancelTimeout = cancelTimeout;
    }

    return () => {
      const settlement = mapSpeciesRestoreSettlementRef.current;
      const isPending =
        settlement.attemptId === restoreAttemptId &&
        settlement.status === "pending";
      releaseMapSpeciesRestoreResources(restoreAttemptId);
      if (isPending) {
        deferMapRestoreCancellation(window, restoreAttemptId, () => {
          settleMapSpeciesRestore(restoreAttemptId, "failure");
        });
      }
    };
  }, [
    mapSpeciesRestoreContext,
    releaseMapSpeciesRestoreResources,
    settleMapSpeciesRestore,
  ]);

  useEffect(() => {
    if (
      !fishipediaRestoreContext ||
      consumedMapRestoreKeyRef.current ===
        fishipediaRestoreContext.restoreAttemptId
    ) {
      return;
    }

    if (mapSpeciesRestoreContext && !mapSpeciesRestoreReady && !mapSpeciesRestoreFailed) {
      return;
    }

    if (
      mapPostRestoreContext &&
      mapPostRestoreReadiness.status === "pending"
    ) {
      return;
    }

    const historyStates = buildFishipediaRestoreHistoryStates(location.state);
    if (
      !historyStates ||
      !claimFishipediaRestoreAttempt(fishipediaRestoreContext)
    ) {
      return;
    }

    consumedMapRestoreKeyRef.current =
      fishipediaRestoreContext.restoreAttemptId;
    const isSuccessfulRestore =
      (Boolean(mapSpeciesRestoreContext) && !mapSpeciesRestoreFailed) ||
      mapPostRestoreReadiness.status === "found";
    const transition = submitFishipediaRestoreHistory({
      navigate,
      location,
      states: historyStates,
      outcome: isSuccessfulRestore ? "success" : "terminal",
      ...(mapSpeciesRestoreFailed
        ? {
            terminalDestination: {
              pathname: "/",
              search: "",
              hash: "",
            },
            terminalState: null,
          }
        : {}),
    });
    if (transition === "fallback" || transition === "ignored") {
      setOverlayFish(null);
      setOverlayOpen(false);
      setSpotSelectedPostInfo(null);
      setSelectedSpot(null);
      return;
    }

    if (mapPostRestoreContext) {
      setSelectedSpot(restoredSelectedSpot);
      setSpotSelectedPostInfo(
        mapPostRestoreReadiness.status === "found"
          ? mapPostRestoreReadiness.post
          : null
      );
    } else if (mapSpeciesRestoreContext && !mapSpeciesRestoreFailed) {
      setOverlayFish({
        name: mapSpeciesRestoreContext.fishName,
        source: "map",
      });
    } else if (mapSpeciesRestoreFailed) {
      setOverlayFish(null);
      setOverlayOpen(false);
    }
  }, [
    fishipediaRestoreContext,
    location,
    location.hash,
    location.key,
    location.pathname,
    location.search,
    location.state,
    mapPostRestoreContext,
    mapPostRestoreReadiness,
    mapSpeciesRestoreContext,
    mapSpeciesRestoreFailed,
    mapSpeciesRestoreReady,
    navigate,
    restoredSelectedSpot,
  ]);

  const friendIds = useMemo(
    () => new Set(viewer?.friends || []),
    [viewer?.friends]
  );
  const followingIds = useMemo(
    () => new Set(viewer?.following || []),
    [viewer?.following]
  );
  const selectedSpeciesFilterSet = useMemo(
    () => new Set(selectedSpeciesFilters),
    [selectedSpeciesFilters]
  );
  const hasSpecificSpeciesFilters = selectedSpeciesFilters.length > 0;
  const selectedTypeOfFishingFilterKey = useMemo(
    () => normalizeFilterKey(selectedTypeOfFishingFilter),
    [selectedTypeOfFishingFilter]
  );
  const selectedWayOfFishingFilterKey = useMemo(
    () => normalizeFilterKey(selectedWayOfFishingFilter),
    [selectedWayOfFishingFilter]
  );

  const currentSpeciesOptions = useMemo(
    () =>
      buildAvailableFilterOptions(
        [...posts, ...catchMarkers],
        getCatchSpeciesValue
      ),
    [posts, catchMarkers]
  );

  const currentTypeOfFishingOptions = useMemo(
    () =>
      buildAvailableFilterOptions(
        [...posts, ...catchMarkers],
        getCatchTypeOfFishingValue
      ),
    [posts, catchMarkers]
  );

  const currentWayOfFishingOptions = useMemo(
    () =>
      buildAvailableFilterOptions(
        [...posts, ...catchMarkers],
        getCatchWayOfFishingValue
      ),
    [posts, catchMarkers]
  );

  const availableSpeciesOptions = useMemo(
    () =>
      buildFishSpeciesFilterOptions(
        fishList,
        currentSpeciesOptions.map((option) => option.label)
      ),
    [fishList, currentSpeciesOptions]
  );

  const availableTypeOfFishingOptions = useMemo(
    () =>
      buildTypeOfFishingFilterOptions(
        fishingMethods,
        currentTypeOfFishingOptions.map((option) => option.label)
      ),
    [fishingMethods, currentTypeOfFishingOptions]
  );

  const availableWayOfFishingOptions = useMemo(
    () =>
      buildWayOfFishingFilterOptions(
        currentWayOfFishingOptions.map((option) => option.label)
      ),
    [currentWayOfFishingOptions]
  );

  const feedSpotCatchMetadataIndex = useMemo(() => {
    const catchMetadataIndex = new Map();

    posts.forEach((post) => {
      const joinKeys = buildSpotJoinKeys({
        coordinates: post?.catch?.coordinates || post?.coordinates,
        userId: post?.userId,
        username: post?.username,
        user: post?.user,
      });
      if (joinKeys.length === 0) {
        return;
      }

      const catchMetadata = buildCatchFilterMetadata(post);

      joinKeys.forEach((joinKey) => {
        if (!catchMetadataIndex.has(joinKey)) {
          catchMetadataIndex.set(joinKey, []);
        }

        catchMetadataIndex.get(joinKey).push(catchMetadata);
      });
    });

    return catchMetadataIndex;
  }, [posts]);

  const markerMatchesSelectedFilters = useMemo(() => {
    const hasAllSelected = selectedMarkerFilters.includes(ALL_MARKER_FILTER);
    const hasNoneSelected = selectedMarkerFilters.includes(NONE_MARKER_FILTER);
    const viewerId = viewer?.id;
    const viewerUsername = viewer?.username;

    return (item) => {
      if (hasAllSelected) return true;
      if (hasNoneSelected) return false;

      const ownerId = item?.userId;
      const ownerUsername = item?.username || item?.user;
      const matchesMine =
        selectedMarkerFilters.includes(MARKER_FILTER_MINE) &&
        ((viewerId && ownerId && ownerId === viewerId) ||
          (!ownerId && viewerUsername && ownerUsername === viewerUsername));
      const matchesFriends =
        selectedMarkerFilters.includes(MARKER_FILTER_FRIENDS) &&
        !!ownerId &&
        friendIds.has(ownerId);
      const matchesFollowing =
        selectedMarkerFilters.includes(MARKER_FILTER_FOLLOWING) &&
        !!ownerId &&
        followingIds.has(ownerId);

      return matchesMine || matchesFriends || matchesFollowing;
    };
  }, [
    friendIds,
    followingIds,
    selectedMarkerFilters,
    viewer?.id,
    viewer?.username,
  ]);

  const catchMatchesSelectedMetadata = useMemo(() => {
    if (
      !hasSpecificSpeciesFilters &&
      !selectedTypeOfFishingFilterKey &&
      !selectedWayOfFishingFilterKey
    ) {
      return () => true;
    }

    return (catchItem) => {
      const metadata = buildCatchFilterMetadata(catchItem);
      const matchesSpecies = hasSpecificSpeciesFilters
        ? Boolean(metadata.speciesKey) &&
          selectedSpeciesFilterSet.has(metadata.speciesKey)
        : true;
      const matchesTypeOfFishing = selectedTypeOfFishingFilterKey
        ? metadata.typeOfFishingKey === selectedTypeOfFishingFilterKey
        : true;
      const matchesWayOfFishing = selectedWayOfFishingFilterKey
        ? metadata.wayOfFishingKey === selectedWayOfFishingFilterKey
        : true;

      return matchesSpecies && matchesTypeOfFishing && matchesWayOfFishing;
    };
  }, [
    hasSpecificSpeciesFilters,
    selectedSpeciesFilterSet,
    selectedTypeOfFishingFilterKey,
    selectedWayOfFishingFilterKey,
  ]);

  const mapSpotMatchesSelectedMetadata = useMemo(() => {
    if (
      !hasSpecificSpeciesFilters &&
      !selectedTypeOfFishingFilterKey &&
      !selectedWayOfFishingFilterKey
    ) {
      return () => true;
    }

    return (spot) => {
      const joinKeys = buildSpotJoinKeys({
        coordinates: spot?.coordinates,
        lat: Number(spot?.lat),
        lng: Number(spot?.lng),
        userId: spot?.userId,
        username: spot?.username,
      });

      return joinKeys.some((joinKey) => {
        const associatedCatchMetadata = feedSpotCatchMetadataIndex.get(joinKey);
        if (!associatedCatchMetadata) {
          return false;
        }

        return associatedCatchMetadata.some((catchMetadata) => {
          const matchesSpecies = hasSpecificSpeciesFilters
            ? Boolean(catchMetadata.speciesKey) &&
              selectedSpeciesFilterSet.has(catchMetadata.speciesKey)
            : true;
          const matchesTypeOfFishing = selectedTypeOfFishingFilterKey
            ? catchMetadata.typeOfFishingKey === selectedTypeOfFishingFilterKey
            : true;
          const matchesWayOfFishing = selectedWayOfFishingFilterKey
            ? catchMetadata.wayOfFishingKey === selectedWayOfFishingFilterKey
            : true;

          return matchesSpecies && matchesTypeOfFishing && matchesWayOfFishing;
        });
      });
    };
  }, [
    hasSpecificSpeciesFilters,
    feedSpotCatchMetadataIndex,
    selectedSpeciesFilterSet,
    selectedTypeOfFishingFilterKey,
    selectedWayOfFishingFilterKey,
  ]);

  const filteredCatchMarkers = useMemo(
    () =>
      catchMarkers.filter(
        (catchItem) =>
          markerMatchesSelectedFilters(catchItem) &&
          catchMatchesSelectedMetadata(catchItem)
      ),
    [catchMarkers, markerMatchesSelectedFilters, catchMatchesSelectedMetadata]
  );

  const filteredMapSpots = useMemo(
    () =>
      mapSpots.filter(
        (spot) =>
          markerMatchesSelectedFilters(spot) &&
          mapSpotMatchesSelectedMetadata(spot)
      ),
    [mapSpots, markerMatchesSelectedFilters, mapSpotMatchesSelectedMetadata]
  );

  const loadBasin = useCallback(
    async (basinId, lat, lng) => {
      console.log("ÃƒÂ°Ã…Â¸Ã…Â¸Ã‚Â¦ BASIN CLICK:", basinId, lat, lng);

      setMarker({ lat, lng });

      try {
        const basePath = `/basins/Bulgaria/${basinId}`;
        const lang = i18n.language?.startsWith("bg") ? "bg" : "en";

        const [rulesMd, speciesJson] = await Promise.all([
          fetch(`${basePath}/rules.${lang}.md`).then((response) => {
            if (!response.ok) {
              throw new Error(`Missing rules: ${response.status}`);
            }
            return response.text();
          }),
          fetch(`${basePath}/species.json`).then((response) => {
            if (!response.ok) {
              throw new Error(`Missing species.json: ${response.status}`);
            }
            return response.json();
          }),
        ]);

        const titleMatch = rulesMd.match(/^##\s+(.+)$/m);
        const localizedName = titleMatch
          ? titleMatch[1].replace(/ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Å“.*/, "").trim()
          : basinId.replace(/_/g, " ");

        setData({
          lat,
          lng,
          placeName: localizedName,
          country: "BG",
          environment: "freshwater",
          isBasin: true,
          basinId,
          basinRulesMarkdown: rulesMd,
          fish: speciesJson?.species || [],
        });

        try {
          const weather = await getWeatherByLocation(lat, lng);

          setData((prev) => ({
            ...prev,
            weather: weather.weather ?? null,
            moon: weather.moon ?? null,
            waterTemp: weather.waterTemp ?? null,
            depth: weather.depth ?? null,
          }));
        } catch (error) {
          console.warn("Weather fetch failed for basin", error);
        }

        setOverlayOpen(true);
        setOverlayTab("info");
      } catch (error) {
        console.error("Failed loading basin assets", basinId, error);
        setOverlayOpen(true);
        setOverlayTab("info");
      }
    },
    [i18n.language]
  );

  const update = useCallback(async (lat, lng) => {
    const requestId = ++latestUpdateRequestRef.current;
    setMarker({ lat, lng });
    setWeatherLoading(true);
    setWeatherError(false);

    let weatherData;
    let speciesData;
    const rulesPromise = getFishingRules(lat, lng);

    try {
      const [weatherResponse, speciesResponse] = await Promise.all([
        getWeatherByLocation(lat, lng),
        getSpeciesByLocation(lat, lng),
      ]);

      weatherData = weatherResponse;
      speciesData = speciesResponse;
    } catch (error) {
      if (requestId !== latestUpdateRequestRef.current) {
        return;
      }
      console.warn("Backend data fetch failed", error);
      setWeatherError(true);
      setWeatherLoading(false);
      return;
    }

    if (requestId !== latestUpdateRequestRef.current) {
      return;
    }

    const finalEnvironment =
      speciesData.environment || weatherData.environment || "marine";
    const finalCountry = speciesData.country || weatherData.country || "GLOBAL";
    const resolvedPlaceName = resolveMapPlaceName(weatherData, speciesData);

    setData({
      lat,
      lng,
      placeName: resolvedPlaceName,
      country: finalCountry,
      environment: finalEnvironment,
      speciesResolvedBucket: speciesData?.resolvedBucket || null,
      speciesContentRef: speciesData?.contentRef || null,
      speciesZoneName: speciesData?.zone_name || "",
      weather: weatherData.weather ?? null,
      moon: weatherData.moon ?? null,
      waterTemp: weatherData.waterTemp ?? null,
      depth: weatherData.depth ?? null,
      fish: speciesData.species || [],
      rules: null,
      rulesLoading: true,
      rulesError: false,
    });

    rulesPromise
      .then(async (rulesData) => {
        let basinContent = null;
        if (isBasinRulesResponse(rulesData)) {
          try {
            basinContent = await loadBasinContentFromRules(rulesData, i18n.language);
          } catch (error) {
            console.warn("Failed loading basin content from rules response", error);
          }
        }

        if (requestId !== latestUpdateRequestRef.current) {
          return;
        }

        setData((prev) => {
          if (!prev || prev.lat !== lat || prev.lng !== lng) {
            return prev;
          }

          const nextData = {
            ...prev,
            rules: rulesData,
            rulesLoading: false,
            rulesError: !rulesData,
          };

          if (!basinContent) {
            return nextData;
          }

          const basinId =
            rulesData?.contentRef?.bucketKey || rulesData?.waterBodyId || prev.basinId;

          return {
            ...nextData,
            placeName: basinContent.displayName || prev.placeName,
            country: rulesData?.countryCode || prev.country,
            environment: "freshwater",
            isBasin: true,
            basinId,
            basinName: basinContent.displayName || rulesData?.waterBodyName || null,
            waterBodyId: rulesData?.waterBodyId || null,
            waterBodyName: rulesData?.waterBodyName || basinContent.displayName || null,
            basinRulesMarkdown: basinContent.rulesMarkdown,
            fish: basinContent.species,
          };
        });
      })
      .catch(() => {
        setData((prev) => {
          if (!prev || prev.lat !== lat || prev.lng !== lng) {
            return prev;
          }

          return {
            ...prev,
            rulesLoading: false,
            rulesError: true,
          };
        });
      });

    setWeatherLoading(false);
  }, [i18n.language]);

  useEffect(() => {
    if (!mapRef.current || !mapReady) return;

    const liveRestoreAttemptId = mapSpeciesRestoreContext?.restoreAttemptId;
    const pendingRestore = readPendingMapRestore(
      sessionStorage,
      liveRestoreAttemptId
    );
    if (
      pendingRestore.status === "accepted" ||
      pendingRestore.status === "not-fishipedia"
    ) {
      return;
    }
    if (pendingRestore.status === "unavailable") {
      if (liveRestoreAttemptId) {
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      }
      return;
    }
    if (
      mapSpeciesRestoreContext &&
      (pendingRestore.status === "stale" ||
        pendingRestore.status === "invalid")
    ) {
      settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      return;
    }

    if (!MapStateManager.shouldRestore()) {
      return;
    }

    const savedState = MapStateManager.load();
    if (!savedState) return;

    console.log("ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Å¾ Restoring map state...", savedState);
    isRestoringRef.current = true;

    mapRef.current.setView([savedState.lat, savedState.lng], savedState.zoom, {
      animate: false,
    });

    if (
      savedState.overlay?.isOpen &&
      typeof savedState.overlay.lat === "number" &&
      typeof savedState.overlay.lng === "number"
    ) {
      setTimeout(() => {
        update(savedState.overlay.lat, savedState.overlay.lng).then(() => {
          setOverlayOpen(true);
          setOverlayTab(savedState.overlay.tab || "weather");
        });
      }, 300);
    }

    setTimeout(() => {
      isRestoringRef.current = false;
    }, 1000);
  }, [
    liveMapSpeciesRestoreAttemptId,
    mapReady,
    mapSpeciesRestoreContext,
    settleMapSpeciesRestore,
    update,
  ]);

  useEffect(() => {
    if (!mapRef.current || isRestoringRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      MapStateManager.save(mapRef.current, {
        isOpen: overlayOpen,
        tab: overlayTab,
        lat: marker?.lat,
        lng: marker?.lng,
        placeName: data?.placeName,
      });
    }, 500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [overlayOpen, overlayTab, marker, data]);

  useEffect(() => {
    const handler = (event) => {
      if (!mapRef.current) return;
      const forcedTab = event?.detail?.overlay?.tab;
      MapStateManager.save(mapRef.current, {
        isOpen: true,
        tab: forcedTab ?? overlayTab,
        lat: marker?.lat,
        lng: marker?.lng,
        placeName: data?.placeName,
      });
    };
    window.addEventListener("saveMapStateNow", handler);
    return () => window.removeEventListener("saveMapStateNow", handler);
  }, [overlayTab, marker, data]);

  const handleMapClick = useCallback(
    (lat, lng) => {
      update(lat, lng);
      setOverlayOpen(true);
      setOverlayTab((prev) => (overlayOpen ? prev : "weather"));
    },
    [overlayOpen, update]
  );

  const handleSearchSelect = useCallback(
    (place) => {
      const lat = parseFloat(place.lat);
      const lng = parseFloat(place.lon);
      setMarker({ lat, lng });
      update(lat, lng);
      setOverlayOpen(true);
      setOverlayTab("weather");

      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], 6, { animate: true, duration: 1.5 });
      }
    },
    [update]
  );

  const waitForStableMap = useCallback(async () => {
    let attempts = 0;

    while (attempts < 30) {
      const map = mapRef.current;
      const mapPane = map?.getPane?.("mapPane") || map?._mapPane;
      const container = map?.getContainer?.();

      if (
        map &&
        mapReady &&
        map._loaded &&
        mapPane &&
        container &&
        container.isConnected
      ) {
        await new Promise((resolve) => {
          map.whenReady(resolve);
        });

        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(resolve);
          });
        });

        if (mapRef.current === map) {
          map.invalidateSize(false);
          return map;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts += 1;
    }

    return null;
  }, [mapReady, mapSpeciesRestoreContext]);

  const flyToLocationSafely = useCallback(
    async ({ lat, lng, zoom = 8, onArrive, signal }) => {
      const map = await waitForStableMap();
      if (!map || signal?.aborted) {
        return false;
      }

      const center = map.getCenter?.();
      const isAlreadyAtTarget =
        center &&
        Math.abs(center.lat - lat) < 0.0001 &&
        Math.abs(center.lng - lng) < 0.0001 &&
        map.getZoom?.() === zoom;

      if (isAlreadyAtTarget) {
        onArrive?.();
        return true;
      }

      let arrived = false;
      const cancelArrival = () => {
        if (arrived) return;
        arrived = true;
        map.off("moveend", finishArrival);
      };
      const finishArrival = () => {
        if (arrived) {
          return;
        }

        arrived = true;
        signal?.removeEventListener("abort", cancelArrival);
        onArrive?.();
      };

      map.once("moveend", finishArrival);
      signal?.addEventListener("abort", cancelArrival, { once: true });

      try {
        map.flyTo([lat, lng], zoom, { animate: true, duration: 2 });
      } catch (error) {
        map.off("moveend", finishArrival);
        signal?.removeEventListener("abort", cancelArrival);
        console.warn("Unable to fly to map location", error);
        return false;
      }

      return true;
    },
    [waitForStableMap]
  );

  const openLocationPermissionDialog = useCallback((reason) => {
    setLocationPermissionDialog({
      reason,
      titleKey:
        GEOLOCATION_PERMISSION_TITLE_KEYS[reason] ||
        GEOLOCATION_PERMISSION_TITLE_KEYS.unavailable,
      messageKey:
        GEOLOCATION_PERMISSION_MESSAGE_KEYS[reason] ||
        GEOLOCATION_PERMISSION_MESSAGE_KEYS.unavailable,
    });
  }, []);

  const requestCurrentLocation = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      console.warn("Geolocation is not available in this browser.");
      openLocationPermissionDialog("unsupported");
      return;
    }

    const applyResolvedPosition = (position) => {
      const lat = Number(position?.coords?.latitude);
      const lng = Number(position?.coords?.longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        console.warn("Geolocation returned invalid coordinates.");
        openLocationPermissionDialog("unavailable");
        return;
      }

      setLocationPermissionDialog(null);
      flyToLocationSafely({ lat, lng, zoom: 12 });
    };

    const getGeolocationErrorReason = (error, permissionState = "unknown") => {
      const errorCode = getGeolocationErrorCode(error);

      if (errorCode === GEOLOCATION_PERMISSION_DENIED) {
        if (permissionState === "denied") {
          return "denied";
        }

        return isMobileLocationBrowser() ? "deviceUnavailable" : "denied";
      }

      if (errorCode === GEOLOCATION_POSITION_UNAVAILABLE) {
        return "deviceUnavailable";
      }

      if (errorCode === GEOLOCATION_TIMEOUT) {
        return "timeout";
      }

      return "unavailable";
    };

    const shouldTryFallbackLocation = (
      error,
      permissionState = "unknown"
    ) => {
      const errorCode = getGeolocationErrorCode(error);

      return (
        errorCode === 0 ||
        errorCode === GEOLOCATION_POSITION_UNAVAILABLE ||
        errorCode === GEOLOCATION_TIMEOUT ||
        (errorCode === GEOLOCATION_PERMISSION_DENIED &&
          permissionState !== "denied" &&
          isMobileLocationBrowser())
      );
    };

    const handleGeolocationFailure = async (primaryError) => {
      console.warn("Unable to locate current position", primaryError);
      const permissionState = await getGeolocationPermissionState();

      if (!shouldTryFallbackLocation(primaryError, permissionState)) {
        openLocationPermissionDialog(
          getGeolocationErrorReason(primaryError, permissionState)
        );
        return;
      }

      try {
        const fallbackPosition = await requestCurrentPosition(
          FALLBACK_GEOLOCATION_OPTIONS
        );
        applyResolvedPosition(fallbackPosition);
      } catch (fallbackError) {
        console.warn(
          "Unable to locate current position on fallback",
          fallbackError
        );
        const fallbackPermissionState = await getGeolocationPermissionState();
        openLocationPermissionDialog(
          getGeolocationErrorReason(fallbackError, fallbackPermissionState)
        );
      }
    };

    navigator.geolocation.getCurrentPosition(
      applyResolvedPosition,
      (error) => {
        void handleGeolocationFailure(error);
      },
      PRIMARY_GEOLOCATION_OPTIONS
    );
  }, [flyToLocationSafely, openLocationPermissionDialog]);

  const handleLocateMe = useCallback(() => {
    requestCurrentLocation();
  }, [requestCurrentLocation]);

  const handleCloseLocationPermissionDialog = useCallback(() => {
    setLocationPermissionDialog(null);
  }, []);

  const handleRetryLocationPermission = useCallback(() => {
    setLocationPermissionDialog(null);
    requestCurrentLocation();
  }, [requestCurrentLocation]);

  useEffect(() => {
    const handleFlyToSpot = async (event) => {
      const {
        lat,
        lng,
        name,
        zoom = 8,
        openSpotWindow,
        privateFavoriteOutlookContext = null,
      } = event.detail;
      const didFly = await flyToLocationSafely({ lat, lng, zoom });
      if (!didFly) return;

      if (openSpotWindow) {
        setSelectedSpot({
          name: name || t("map_fishing_spot"),
          location: t("map_custom_location"),
          lat,
          lng,
          reviews: [],
          privateFavoriteOutlookContext,
        });

        setTimeout(() => {
          const nearbyPosts = posts.filter((post) => {
            if (
              !post.catch?.coordinates ||
              post.catch.coordinates === "ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â"
            ) {
              return false;
            }
            const match = post.catch.coordinates.match(/(-?\d+(\.\d+)?)/g);
            if (!match || match.length < 2) return false;
            const postLat = parseFloat(match[0]);
            const postLng = parseFloat(match[1]);
            return (
              Math.abs(postLat - lat) < 0.002 && Math.abs(postLng - lng) < 0.002
            );
          });

          let locationName = name || t("map_fishing_spot");
          let locationDesc = t("map_custom_location");

          if (nearbyPosts.length > 0 && nearbyPosts[0].catch?.location) {
            locationDesc = nearbyPosts[0].catch.location;
          }

          setSelectedSpot((current) =>
            current?.privateFavoriteOutlookContext?.spotKey ===
            privateFavoriteOutlookContext?.spotKey
              ? {
                  name: locationName,
                  location: locationDesc,
                  lat,
                  lng,
                  reviews: nearbyPosts,
                  privateFavoriteOutlookContext,
                }
              : current
          );
        }, 1200);
      }
    };

    window.addEventListener("flyToSpot", handleFlyToSpot);
    return () => window.removeEventListener("flyToSpot", handleFlyToSpot);
  }, [flyToLocationSafely, posts, t]);

  useEffect(() => {
    if (!mapReady) return;

    const liveRestoreAttemptId = mapSpeciesRestoreContext?.restoreAttemptId;
    const ownedRead = readPendingMapRestore(
      sessionStorage,
      liveRestoreAttemptId
    );

    if (ownedRead.status === "unavailable") {
      if (liveRestoreAttemptId) {
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      }
      return;
    }
    if (ownedRead.status === "missing") return;
    if (ownedRead.status === "stale") {
      // A leftover or newer owned record is harmless without a matching live
      // attempt and must never be flown or erased by this attempt.
      if (liveRestoreAttemptId) {
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      }
      return;
    }
    if (ownedRead.status === "invalid") {
      if (liveRestoreAttemptId) {
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      } else {
        clearPendingMapLocationStorage(null, ownedRead.raw);
      }
      return;
    }

    const isFishipediaRestore = ownedRead.status === "accepted";
    let raw = ownedRead.raw;
    let pendingLocation = ownedRead.record;

    if (ownedRead.status === "not-fishipedia") {
      if (mapSpeciesRestoreContext) {
        // A separate legitimate operation replaced this attempt's record.
        // Fail this attempt without deleting the newer shared-key operation.
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
        return;
      }

      try {
        pendingLocation = JSON.parse(raw);
      } catch (error) {
        clearPendingMapLocationStorage(null, raw);
        console.warn("Invalid pendingMapLocation", error);
        return;
      }
    }

    if (!raw || pendingMapLocationRef.current === raw || !pendingLocation) return;

    try {
      const lat = pendingLocation.lat;
      const lng = pendingLocation.lng;
      const zoom =
        pendingLocation.zoom === undefined
          ? DEFAULT_PENDING_MAP_LOCATION_ZOOM
          : pendingLocation.zoom;
      const shouldOpenOverlay = pendingLocation.openOverlay !== false;
      const pendingOverlayTab =
        pendingLocation.tab === "fish" ? "fish" : "weather";

      if (
        typeof lat !== "number" ||
        !Number.isFinite(lat) ||
        lat < -90 ||
        lat > 90 ||
        typeof lng !== "number" ||
        !Number.isFinite(lng) ||
        lng < -180 ||
        lng > 180 ||
        typeof zoom !== "number" ||
        !Number.isFinite(zoom) ||
        zoom < 2 ||
        zoom > 18
      ) {
        throw new Error("Invalid pending map coordinates");
      }

      pendingMapLocationRef.current = raw;

      const restoreController = isFishipediaRestore
        ? mapSpeciesRestoreResourcesRef.current.attemptId ===
          liveRestoreAttemptId
          ? mapSpeciesRestoreResourcesRef.current.controller
          : null
        : null;

      flyToLocationSafely({
        lat,
        lng,
        zoom,
        signal: restoreController?.signal,
        onArrive: () => {
          if (
            isFishipediaRestore &&
            !settleMapSpeciesRestore(liveRestoreAttemptId, "success")
          ) {
            return;
          }

          setMarker({ lat, lng });
          setSelectedSpot(null);

          if (shouldOpenOverlay) {
            update(lat, lng);
            setOverlayOpen(true);
            setOverlayTab(pendingOverlayTab);
            return;
          }

          setData(null);
          setWeatherLoading(false);
          setWeatherError(false);
          setOverlayOpen(false);
          setOverlayTab("weather");
          setOverlayPanelView("tabs");
          setOverlayFish(null);
          setSpotReviewsOpen(false);
          setSpotSelectedPostInfo(null);
          setSpotDangerOpen(false);
          setSpotExpandedImage(null);
          setSpotExpandedImageSource(null);
          setSpotShareOpen(false);
          setSpotLocationConsentOpen(false);

          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event(REFRESH_APP_CHROME_METRICS_EVENT));
          }
        },
      }).then((didFly) => {
        if (didFly) {
          if (!isFishipediaRestore) {
            clearPendingMapLocationStorage(null, raw);
            pendingMapLocationRef.current = "";
          }
          return;
        }

        if (isFishipediaRestore) {
          const activeResources = mapSpeciesRestoreResourcesRef.current;
          if (
            !restoreController ||
            restoreController.signal.aborted ||
            activeResources.controller !== restoreController
          ) {
            return;
          }
          settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
        } else if (pendingMapLocationRef.current === raw) {
          pendingMapLocationRef.current = "";
          clearPendingMapLocationStorage(null, raw);
        }
      });
    } catch (error) {
      if (isFishipediaRestore || mapSpeciesRestoreContext) {
        settleMapSpeciesRestore(liveRestoreAttemptId, "failure");
      } else {
        pendingMapLocationRef.current = "";
        clearPendingMapLocationStorage(null, raw);
      }
      console.warn("Invalid pendingMapLocation", error);
    }
  }, [
    flyToLocationSafely,
    liveMapSpeciesRestoreAttemptId,
    mapReady,
    mapSpeciesRestoreContext,
    settleMapSpeciesRestore,
    update,
  ]);

  useEffect(() => {
    const handler = async (event) => {
      const { lat, lng, zoom = 8 } = event.detail;
      const shouldOpenOverlay = event.detail?.openOverlay !== false;

      await flyToLocationSafely({
        lat,
        lng,
        zoom,
        onArrive: () => {
          setMarker({ lat, lng });
          setSelectedSpot(null);

          if (shouldOpenOverlay) {
            update(lat, lng);
            setOverlayOpen(true);
            setOverlayTab("weather");
            return;
          }

          setData(null);
          setWeatherLoading(false);
          setWeatherError(false);
          setOverlayOpen(false);
          setOverlayTab("weather");
          setOverlayPanelView("tabs");
          setOverlayFish(null);
          setSpotReviewsOpen(false);
          setSpotSelectedPostInfo(null);
          setSpotDangerOpen(false);
          setSpotExpandedImage(null);
          setSpotExpandedImageSource(null);
          setSpotShareOpen(false);
          setSpotLocationConsentOpen(false);
        },
      });
    };

    window.addEventListener("openMapAtLocation", handler);
    return () => window.removeEventListener("openMapAtLocation", handler);
  }, [flyToLocationSafely, update]);

  useEffect(() => {
    async function loadCatches() {
      try {
        const token = localStorage.getItem("token");
        if (!token && !viewer?.id) {
          setCatchMarkers([]);
          return;
        }

        const res = await fetch(
          `${API_BASE}/trips/`,
          withApiCredentials({
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        );

        if (!res.ok) {
          setCatchMarkers([]);
          return;
        }

        const trips = await res.json();
        if (!Array.isArray(trips)) {
          setCatchMarkers([]);
          return;
        }

        const all = [];
        trips.forEach((trip) => {
          if (Array.isArray(trip.catches)) {
            trip.catches.forEach((catchItem) => {
              if (!catchItem.coordinates) return;
              all.push({
                ...catchItem,
                tripId: trip.id,
                tripName: trip.name,
                userId: trip.userId,
                imageUrl: catchItem.imagePath
                  ? `${API_BASE}${catchItem.imagePath}`
                  : null,
              });
            });
          }
        });
        setCatchMarkers(all);
      } catch (err) {
        setCatchMarkers([]);
      }
    }

    loadCatches();
    const handler = () => loadCatches();
    const refreshEvent = getCatchSubmitRefreshEventName();
    window.addEventListener("refreshCatches", handler);
    window.addEventListener(refreshEvent, handler);
    return () => {
      window.removeEventListener("refreshCatches", handler);
      window.removeEventListener(refreshEvent, handler);
    };
  }, [viewer?.id]);

  useEffect(() => {
    let isMounted = true;

    async function loadCatchMetadataSources() {
      try {
        const [loadedFishingMethods, loadedFishList] = await Promise.all([
          fetch(`${process.env.PUBLIC_URL}/JSON/fishingMethods.json`).then(
            (res) => res.json()
          ),
          fetch("/JSON/fish-data.json").then((res) => res.json()),
        ]);

        if (!isMounted) {
          return;
        }

        setFishingMethods(
          loadedFishingMethods && typeof loadedFishingMethods === "object"
            ? loadedFishingMethods
            : {}
        );
        setFishList(Array.isArray(loadedFishList) ? loadedFishList : []);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setFishingMethods({});
        setFishList([]);
        console.error("Failed to load catch metadata sources:", err);
      }
    }

    loadCatchMetadataSources();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    async function loadSpots() {
      try {
        const token = localStorage.getItem("token");
        if (!token && !viewer?.id) return;

        const res = await fetch(
          `${API_BASE}/map/spots`,
          withApiCredentials({
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        );

        if (!res.ok) throw new Error("Failed loading map spots");
        const loadedData = await res.json();
        setMapSpots(loadedData);
      } catch (err) {
        console.error("ÃƒÂ¢Ã‚ÂÃ…â€™ Cannot load map spots:", err);
      }
    }

    loadSpots();
    const handler = () => loadSpots();
    const refreshEvent = getCatchSubmitRefreshEventName();
    window.addEventListener("spotsUpdated", handler);
    window.addEventListener(refreshEvent, handler);
    return () => {
      window.removeEventListener("spotsUpdated", handler);
      window.removeEventListener(refreshEvent, handler);
    };
  }, [viewer?.id]);

  const handleBasinClick = useCallback(
    (basin) => {
      loadBasin(basin.id, basin.lat, basin.lng);
    },
    [loadBasin]
  );

  const resetOverlayDetailState = useCallback(() => {
    setOverlayPanelView("tabs");
    setOverlayFish(null);
  }, []);

  const resetSelectedSpotDetailState = useCallback(() => {
    setSpotReviewsOpen(false);
    setSpotSelectedPostInfo(null);
    setSpotDangerOpen(false);
    setSpotExpandedImage(null);
    setSpotExpandedImageSource(null);
    setSpotShareOpen(false);
    setSpotLocationConsentOpen(false);
  }, []);

  useEffect(() => {
    const handleClearMapSelection = () => {
      const restoreAttemptId = mapSpeciesRestoreContext?.restoreAttemptId;
      if (restoreAttemptId) {
        if (!settleMapSpeciesRestore(restoreAttemptId, "failure")) return;
      } else {
        pendingMapLocationRef.current = "";
        clearPendingMapLocationStorage();
      }
      setMarker(null);
      setData(null);
      setWeatherLoading(false);
      setWeatherError(false);
      setOverlayOpen(false);
      setOverlayTab("weather");
      resetOverlayDetailState();
      setSelectedSpot(null);
      resetSelectedSpotDetailState();
    };

    window.addEventListener(CLEAR_MAP_SELECTION_EVENT, handleClearMapSelection);
    return () => {
      window.removeEventListener(CLEAR_MAP_SELECTION_EVENT, handleClearMapSelection);
    };
  }, [
    mapSpeciesRestoreContext?.restoreAttemptId,
    resetOverlayDetailState,
    resetSelectedSpotDetailState,
    settleMapSpeciesRestore,
  ]);

  const closeOverlayPanelView = useCallback(() => {
    setOverlayPanelView("tabs");
  }, []);

  const closeOverlayFish = useCallback(() => {
    setOverlayFish(null);
  }, []);

  const closeSpotReviews = useCallback(() => {
    setSpotReviewsOpen(false);
  }, []);

  const closeSpotDanger = useCallback(() => {
    setSpotDangerOpen(false);
  }, []);

  const closeSpotShare = useCallback(() => {
    setSpotShareOpen(false);
  }, []);

  const closeSpotLocationConsent = useCallback(() => {
    setSpotLocationConsentOpen(false);
  }, []);

  const closeSpotSelectedPostInfo = useCallback(() => {
    setSpotSelectedPostInfo(null);
  }, []);

  const navigateToFishipediaFromMapChild = useCallback(
    ({ slug, returnContext, closeChild }) => {
      if (!slug || !returnContext) return;

      mapHistoryRef.current?.requestSuppressNavigationSync();
      closeChild?.();

      navigate(`/fishipedia#${slug}`, {
        replace: true,
        state: returnContext.historyTransaction
          ? { [FISHIPEDIA_RETURN_CONTEXT_KEY]: returnContext }
          : null,
      });
    },
    [navigate]
  );

  const handleMapSpeciesFishipediaNavigate = useCallback(
    ({ slug, fishName, mapState: selectedMapState }) => {
      const mapState = resolveFishipediaMapState(
        mapRef.current,
        selectedMapState,
        MapStateManager.load()
      );
      const returnContext = createMapSpeciesFishipediaReturnContext({
        fishName,
        mapState,
        historyLocation: location,
      });
      if (!returnContext) return;

      navigateToFishipediaFromMapChild({
        slug,
        returnContext,
        closeChild: () => setOverlayFish(null),
      });
    },
    [location, navigateToFishipediaFromMapChild]
  );

  const handleSpotPostInfoNavigateAway = useCallback(
    (destination) => {
      if (destination?.type !== "fishipedia" || !destination.slug) return;

      const returnContext = createPostInfoFishipediaReturnContext({
        post: spotSelectedPostInfo,
        surface: "map",
        selectedSpot,
        historyLocation: location,
      });

      if (!returnContext) return;

      navigateToFishipediaFromMapChild({
        slug: destination.slug,
        returnContext,
        closeChild: () => setSpotSelectedPostInfo(null),
      });
    },
    [
      navigateToFishipediaFromMapChild,
      location,
      selectedSpot,
      spotSelectedPostInfo,
    ]
  );

  const closeSpotExpandedImage = useCallback(() => {
    setSpotExpandedImage(null);
    setSpotExpandedImageSource(null);
  }, []);

  const mapHistoryItems = useMemo(
    () => [
      {
        id: "weather-overlay",
        isOpen: overlayOpen,
        close: () => {
          setOverlayOpen(false);
          resetOverlayDetailState();
        },
      },
      {
        id: "weather-panel",
        isOpen: overlayOpen && overlayPanelView !== "tabs",
        close: closeOverlayPanelView,
      },
      {
        id: "weather-fish",
        isOpen: overlayOpen && Boolean(overlayFish),
        close: closeOverlayFish,
      },
      {
        id: "spot-legend",
        isOpen: Boolean(selectedSpot),
        close: () => {
          setSelectedSpot(null);
          resetSelectedSpotDetailState();
        },
      },
      {
        id: "spot-reviews",
        isOpen: Boolean(selectedSpot) && spotReviewsOpen,
        close: closeSpotReviews,
      },
      {
        id: "spot-danger",
        isOpen: Boolean(selectedSpot) && spotDangerOpen,
        close: closeSpotDanger,
      },
      {
        id: "spot-share",
        isOpen: Boolean(selectedSpot) && spotShareOpen,
        close: closeSpotShare,
      },
      {
        id: "spot-location-consent",
        isOpen: Boolean(selectedSpot) && spotLocationConsentOpen,
        close: closeSpotLocationConsent,
      },
      {
        id: "spot-post-info",
        isOpen: Boolean(selectedSpot) && Boolean(spotSelectedPostInfo),
        close: closeSpotSelectedPostInfo,
      },
      {
        id: "spot-fullscreen-image",
        isOpen: Boolean(selectedSpot) && Boolean(spotExpandedImage),
        close: closeSpotExpandedImage,
      },
    ],
    [
      closeOverlayFish,
      closeOverlayPanelView,
      closeSpotDanger,
      closeSpotExpandedImage,
      closeSpotLocationConsent,
      closeSpotReviews,
      closeSpotSelectedPostInfo,
      closeSpotShare,
      overlayFish,
      overlayOpen,
      overlayPanelView,
      resetOverlayDetailState,
      resetSelectedSpotDetailState,
      selectedSpot,
      spotDangerOpen,
      spotExpandedImage,
      spotLocationConsentOpen,
      spotReviewsOpen,
      spotSelectedPostInfo,
      spotShareOpen,
    ]
  );

  const mapHistory = useLocationStateStack(
    "mapOverlayStack",
    mapHistoryItems,
    {
      deferSynchronization:
        Boolean(fishipediaRestoreContext) ||
        isFishipediaHistoryTransitionPending("mapOverlayStack"),
    }
  );
  mapHistoryRef.current = mapHistory;
  const { requestReplaceSync: requestMapHistoryReplace } = mapHistory;

  const handleSpotClick = useCallback((updateFn) => {
    setSelectedSpot((prev) =>
      typeof updateFn === "function" ? updateFn(prev) : updateFn
    );
  }, []);

  const handleCloseSelectedSpot = useCallback(() => {
    setSelectedSpot(null);
    resetSelectedSpotDetailState();
  }, [resetSelectedSpotDetailState]);

  const handleOverlayClose = useCallback((reason) => {
    setOverlayOpen(false);
    resetOverlayDetailState();
    if (reason === "user") {
      setOverlayTab("weather");
    }
  }, [resetOverlayDetailState]);

  useEffect(() => {
    const nextOverlayKey = buildMapOverlayKey(marker, overlayOpen);
    const previousOverlayKey = previousOverlayKeyRef.current;
    const overlayHasNestedState =
      overlayPanelView !== "tabs" || Boolean(overlayFish);

    if (
      previousOverlayKey &&
      previousOverlayKey !== nextOverlayKey &&
      overlayHasNestedState
    ) {
      requestMapHistoryReplace();
      resetOverlayDetailState();
    }

    previousOverlayKeyRef.current = nextOverlayKey;
  }, [
    marker,
    overlayFish,
    overlayOpen,
    overlayPanelView,
    requestMapHistoryReplace,
    resetOverlayDetailState,
  ]);

  useEffect(() => {
    const nextSelectedSpotKey = buildSelectedSpotKey(selectedSpot);
    const previousSelectedSpotKey = previousSelectedSpotKeyRef.current;
    const selectedSpotHasNestedState =
      spotReviewsOpen ||
      Boolean(spotSelectedPostInfo) ||
      spotDangerOpen ||
      Boolean(spotExpandedImage) ||
      spotShareOpen ||
      spotLocationConsentOpen;

    if (
      previousSelectedSpotKey &&
      previousSelectedSpotKey !== nextSelectedSpotKey &&
      selectedSpotHasNestedState
    ) {
      requestMapHistoryReplace();
      resetSelectedSpotDetailState();
    }

    previousSelectedSpotKeyRef.current = nextSelectedSpotKey;
  }, [
    requestMapHistoryReplace,
    resetSelectedSpotDetailState,
    selectedSpot,
    spotDangerOpen,
    spotExpandedImage,
    spotLocationConsentOpen,
    spotReviewsOpen,
    spotSelectedPostInfo,
    spotShareOpen,
  ]);

  return {
    activePage,
    posts,
    onOpenProfile,
    onOpenGroup,
    onAddFavoriteSpot,
    viewer,
    onUserUpdated,
    isSimpleModeActive,
    mapRef,
    mapReady,
    setMapReady,
    selectedMarkerFilters,
    setSelectedMarkerFilters,
    selectedSpeciesFilters,
    setSelectedSpeciesFilters,
    selectedTypeOfFishingFilter,
    setSelectedTypeOfFishingFilter,
    selectedWayOfFishingFilter,
    setSelectedWayOfFishingFilter,
    availableSpeciesOptions,
    availableTypeOfFishingOptions,
    availableWayOfFishingOptions,
    filteredCatchMarkers,
    filteredMapSpots,
    marker,
    data,
    weatherLoading,
    weatherError,
    overlayOpen,
    overlayTab,
    setOverlayTab,
    overlayPanelView,
    setOverlayPanelView,
    overlayFish,
    setOverlayFish,
    handleMapSpeciesFishipediaNavigate,
    selectedSpot,
    spotReviewsOpen,
    setSpotReviewsOpen,
    spotSelectedPostInfo,
    setSpotSelectedPostInfo,
    handleSpotPostInfoNavigateAway,
    spotDangerOpen,
    setSpotDangerOpen,
    spotExpandedImage,
    setSpotExpandedImage,
    spotExpandedImageSource,
    setSpotExpandedImageSource,
    spotShareOpen,
    setSpotShareOpen,
    spotLocationConsentOpen,
    setSpotLocationConsentOpen,
    locationPermissionDialog,
    handleCloseLocationPermissionDialog,
    handleRetryLocationPermission,
    handleMapClick,
    handleSearchSelect,
    handleLocateMe,
    handleBasinClick,
    handleSpotClick,
    handleCloseSelectedSpot,
    handleOverlayClose,
  };
}
